import React, { useCallback, useEffect, useState } from 'react';
import { api, formatDate } from '../lib/api';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, UserCog, Trash2, Save, KeyRound, Copy, Check, Edit3 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { canSeeInternalNotes, driveTodo, isAdmin, normalizeRole, roleLabel, rolEntraEnLeads, tieneChat } from '../lib/roles';
import { esCuentaDeTablet } from '../lib/modoTablet';
import { loginDe } from '../lib/login';
import { useNavigate } from 'react-router';

// LOS SEIS ROLES, no tres (dirección, 2026-09-21: «al entrar en usuarios no me
// deja asignarle rol de doctor»). El servidor acepta desde hace tiempo los
// seis —models.USER_ROLE: admin, direccion, administrativo, doctor,
// enfermeria, anestesista— pero esta lista se quedó con los tres de
// administración. No es que la pantalla lo rechazara: es que no estaba, así
// que a una doctora nueva no había forma de ponerle su rol desde aquí y había
// que tocarlo por detrás. Los nombres salen de lib/roles.ROLES; aquí se les
// añade en una línea QUÉ VE cada uno, que es lo que se decide al elegir.
const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin · acceso total y gestión de usuarios' },
  { value: 'direccion', label: 'Dirección · acceso completo (cobros, KPIs, exports)' },
  { value: 'administrativo', label: 'Administrativo · operativa diaria sin visión global' },
  { value: 'doctor', label: 'Doctor · su agenda, sus pacientes y su historia clínica' },
  { value: 'enfermeria', label: 'Enfermería · quirófano y consulta, sin cobros ni KPIs' },
  { value: 'anestesista', label: 'Anestesista · solo Quirófano y Anestesia' },
];

/**
 * El largo mínimo de la contraseña que el administrador puede poner: 8 al
 * equipo y 6 a las cuentas de tablet (dirección, 29/9/2026: «un usuario
 * único, tablet, con una contraseña corta fija»). Es la misma regla que pone
 * el servidor (`auth_router._exigir_largo_minimo`); aquí solo se dice antes
 * de mandarla. Y la corta de la tablet solo vale desde la conexión de la
 * clínica (Configuración → Conexiones de la clínica).
 */
export const LARGO_MINIMO_EQUIPO = 8;
export const LARGO_MINIMO_TABLET = 6;

export function largoMinimoDeClave(usuario) {
  return esCuentaDeTablet(usuario) ? LARGO_MINIMO_TABLET : LARGO_MINIMO_EQUIPO;
}

function claveCorta(usuario, clave) {
  const minimo = largoMinimoDeClave(usuario);
  if ((clave || '').length >= minimo) return '';
  return esCuentaDeTablet(usuario)
    ? `La contraseña de la tablet debe tener al menos ${minimo} caracteres.`
    : `La contraseña debe tener al menos ${minimo} caracteres (solo las tablets admiten ${LARGO_MINIMO_TABLET}).`;
}

const AVISO_CLAVE_CORTA_TABLET = 'Si tiene menos de 8 caracteres, solo vale desde la conexión de la '
  + 'clínica (Configuración → Conexiones de la clínica).';

/** Contraseña provisional aleatoria (el usuario la cambia en el primer login).
 *  Sin caracteres ambiguos (0/O, 1/l/I). No usamos una fija tipo "123456". */
function randomPwd(n = 8) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let out = '';
  if (window.crypto?.getRandomValues) {
    const arr = new Uint32Array(n);
    window.crypto.getRandomValues(arr);
    for (let i = 0; i < n; i++) out += chars[arr[i] % chars.length];
  } else {
    for (let i = 0; i < n; i++) out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

function UserDialog({ open, onOpenChange, initial, onSaved }) {
  const { user: yo } = useAuth();
  const isEdit = !!initial?.id;
  // La cuenta del iPad (29/9/2026): su rol no está en la lista (no se da de
  // alta desde aquí: `USER_ROLE` no lo admite) y mandarlo daba 422, así que
  // al editarla no se toca el rol. Y su contraseña puede ser de 6.
  const esTablet = isEdit && esCuentaDeTablet(initial);
  const initialRole = initial ? normalizeRole(initial.role) : 'administrativo';
  const [form, setForm] = useState(initial ? { ...initial, role: initialRole } : { email: '', name: '', role: 'administrativo', password: randomPwd() });

  useEffect(() => {
    if (open) {
      const i = initial
        ? { ...initial, role: normalizeRole(initial.role) }
        : { email: '', name: '', role: 'administrativo', password: randomPwd() };
      setForm(i);
    }
  }, [open, initial]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // «Leads: solo de estas campañas» (administración, 22/10/2026): las
  // campañas de Campañas, para elegir de cuáles ve los leads esta persona.
  // Solo id, nombre y estado (GET /api/users/campanas-leads): ni gasto ni
  // contactos. Si no se pueden leer, no se toca lo que tenía.
  const [campanasLeads, setCampanasLeads] = useState({ estado: 'cargando', lista: [] });
  useEffect(() => {
    if (!open || !isEdit || esTablet) return undefined;
    let vivo = true;
    setCampanasLeads({ estado: 'cargando', lista: [] });
    api.get('/api/users/campanas-leads')
      .then((r) => {
        if (vivo) setCampanasLeads({ estado: 'ok', lista: Array.isArray(r.data?.campanas) ? r.data.campanas : [] });
      })
      .catch(() => { if (vivo) setCampanasLeads({ estado: 'fallo', lista: [] }); });
    return () => { vivo = false; };
  }, [open, isEdit, esTablet]);
  // Solo a quien su rol NO entra en Leads (22/10/2026): un rol de gestión ya
  // ve todos los leads y el servidor no se lo deja poner. Con el rol que va a
  // tener, el elegido en el desplegable.
  const ofreceLeadsCampanas = isEdit && !esTablet && !rolEntraEnLeads(form.role);
  const leadsCampanas = Array.isArray(form.leads_campanas) ? form.leads_campanas : [];
  const alternarCampanaLeads = (id) => setForm((f) => {
    const actuales = Array.isArray(f.leads_campanas) ? f.leads_campanas : [];
    return { ...f, leads_campanas: actuales.includes(id) ? actuales.filter((x) => x !== id) : [...actuales, id] };
  });
  // Las de Campañas y, si tenía guardada alguna que ya no está, también, para
  // poder quitársela.
  const opcionesCampanasLeads = [
    ...campanasLeads.lista,
    ...leadsCampanas.filter((id) => !campanasLeads.lista.some((c) => c.id === id))
      .map((id) => ({ id, nombre: '(campaña que ya no está en Campañas)', estado: '' })),
  ];
  // «Notas de administración: también esta persona» (27/10/2026): solo a
  // quien su rol NO tiene las notas de administración (doctor, enfermería,
  // anestesista); a gestión ya le tocan por rol y el servidor no la deja
  // poner. Con el rol que va a tener, el elegido en el desplegable.
  // Solo a los roles que abren fichas (el servidor rechaza los demás con 400).
  const ofreceNotasAdministracion = isEdit && !esTablet && !canSeeInternalNotes({ role: form.role })
    && ['doctor', 'enfermeria', 'anestesista'].includes((form.role || '').toLowerCase());
  // «Drive: entra en estas carpetas» (27/10/2026): las carpetas del Drive
  // (GET /api/drive/carpetas, que el admin ya lee), para elegir en cuáles
  // entra esta persona. Solo a quien su rol no tiene Drive (el admin lo tiene
  // todo). Si no se pueden leer, no se toca lo que tenía.
  const [carpetasDrive, setCarpetasDrive] = useState({ estado: 'cargando', lista: [] });
  useEffect(() => {
    if (!open || !isEdit || esTablet) return undefined;
    let vivo = true;
    setCarpetasDrive({ estado: 'cargando', lista: [] });
    api.get('/api/drive/carpetas')
      .then((r) => {
        if (vivo) setCarpetasDrive({ estado: 'ok', lista: Array.isArray(r.data) ? r.data : [] });
      })
      .catch(() => { if (vivo) setCarpetasDrive({ estado: 'fallo', lista: [] }); });
    return () => { vivo = false; };
  }, [open, isEdit, esTablet]);
  const ofreceDriveCarpetas = isEdit && !esTablet && !isAdmin({ role: form.role });
  // «Drive: todo (como el administrador)» (3/11/2026): la misma gente que las
  // carpetas (quien su rol no tiene Drive). Marcada, entra en el Drive entero
  // y las carpetas elegidas no cuentan: la lista se tapa, sin borrarlas (al
  // quitar la casilla vuelven a contar).
  const ofreceDriveTodo = ofreceDriveCarpetas;
  const driveTodoMarcado = ofreceDriveTodo && !!form.drive_todo;
  const driveCarpetas = Array.isArray(form.drive_carpetas) ? form.drive_carpetas : [];
  const alternarCarpetaDrive = (id) => setForm((f) => {
    const actuales = Array.isArray(f.drive_carpetas) ? f.drive_carpetas : [];
    return { ...f, drive_carpetas: actuales.includes(id) ? actuales.filter((x) => x !== id) : [...actuales, id] };
  });
  // Cada carpeta con su ruta legible («Facturas / 2026»), ordenadas; y si
  // tenía guardada alguna que ya no está, también, para poder quitársela.
  const opcionesCarpetasDrive = (() => {
    const porId = Object.fromEntries(carpetasDrive.lista.map((c) => [c.id, c]));
    const rutaDe = (id) => {
      const partes = [];
      let cursor = id;
      let guarda = 0;
      while (cursor && porId[cursor] && guarda < 20) {
        partes.unshift(porId[cursor].name);
        cursor = porId[cursor].parent_id;
        guarda += 1;
      }
      return partes.join(' / ');
    };
    return [
      ...carpetasDrive.lista.map((c) => ({ id: c.id, ruta: rutaDe(c.id) }))
        .sort((a, b) => a.ruta.localeCompare(b.ruta)),
      ...driveCarpetas.filter((id) => !porId[id])
        .map((id) => ({ id, ruta: '(carpeta que ya no está en el Drive)' })),
    ];
  })();
  // La casilla «Chat de dirección» (16/10/2026): a quien entra al chat por su
  // rol y no es admin (los admin ya están en esa sala). Con el rol que va a
  // tener, el elegido en el desplegable.
  const ofreceChatDireccion = isEdit && !esTablet
    && (form.role || '').toLowerCase() !== 'admin' && tieneChat({ role: form.role });
  // «Edita las tarifas de externos» (23/10/2026): la única llave para cambiar
  // el bloque «Alquiler quirófano externos» del Catálogo. A quien entra en el
  // Catálogo (el doctor no entra; la tablet, nunca). Ser admin no la da.
  const ofreceTarifasExternos = isEdit && !esTablet && (form.role || '').toLowerCase() !== 'doctor';
  // Nadie se la pone a sí mismo (revisión del 24/10/2026; el servidor da
  // 403): en su PROPIA ficha y sin tenerla, se ve bloqueada (salvo el jefe).
  // Quitársela a uno mismo sí, y a otra persona se la pone o quita cualquier
  // admin.
  const soyJefe = yo?.es_jefe === true;
  const tarifasBloqueada = !soyJefe && !!yo?.id && initial?.id === yo.id
    && initial?.edita_tarifas_externos !== true;
  // «Gestiona los bloques del Catálogo» (28/10/2026): la única llave para
  // crear, renombrar y dar de baja los bloques principales del Catálogo de
  // precios. La misma regla que la de las tarifas de externos: a quien entra
  // en el Catálogo, se la da otro admin, nadie a sí mismo (salvo el jefe).
  const ofreceBloquesCatalogo = ofreceTarifasExternos;
  const bloquesBloqueada = !soyJefe && !!yo?.id && initial?.id === yo.id
    && initial?.gestiona_bloques_catalogo !== true;

  const submit = async (e) => {
    e.preventDefault();
    if (form.password || !isEdit) {
      // El rol que cuenta es el que va a tener (el de la tablet no cambia).
      const corta = claveCorta(esTablet ? initial : { role: form.role }, form.password);
      if (corta) { toast.error(corta); return; }
    }
    try {
      if (isEdit) {
        // Sin `puede_borrar_episodios` (D4, dirección, 22/09/2026): borrar
        // episodios es del rol admin y la marca por persona ya no cuenta.
        const payload = { name: form.name, apodo: (form.apodo || '').trim(),
          agente_notas: (form.agente_notas || '').trim() };
        if (!esTablet) payload.role = form.role;
        // Quién sale en Indicadores (dirección, 04/10/2026): siempre se manda,
        // también apagada, para poder quitársela a alguien. La tablet no es
        // una persona: ni casilla ni campo.
        if (!esTablet) payload.en_indicadores = !!form.en_indicadores;
        // Sala de dirección del chat sin ser admin (dirección, 16/10/2026): solo
        // se manda a quien la casilla se le enseña; al admin ya le toca por rol.
        if (ofreceChatDireccion) payload.chat_direccion = !!form.chat_direccion;
        // «Mejoras software» (20/10/2026): a cualquier persona del equipo,
        // también a un admin (su rol NO le da la página: solo la casilla).
        // Siempre se manda, también apagada, para poder quitársela.
        if (!esTablet) payload.mejoras_software = !!form.mejoras_software;
        // «Edita las tarifas de externos» (23/10/2026): a quien se le enseña,
        // siempre, también apagada, para poder quitársela.
        if (ofreceTarifasExternos && !tarifasBloqueada) payload.edita_tarifas_externos = !!form.edita_tarifas_externos;
        // «Gestiona los bloques del Catálogo» (28/10/2026): igual.
        if (ofreceBloquesCatalogo && !bloquesBloqueada) payload.gestiona_bloques_catalogo = !!form.gestiona_bloques_catalogo;
        // «Leads: solo de estas campañas» (22/10/2026): siempre se manda, también
        // vacía, para poder quitárselas. Si las campañas no se pudieron leer,
        // va lo que tenía (no cambia nada). A un rol de gestión, vacía: no se
        // le pone, y si lo tenía guardado, se le quita.
        if (!esTablet) payload.leads_campanas = ofreceLeadsCampanas ? leadsCampanas : [];
        // «Notas de administración: también esta persona» (27/10/2026):
        // siempre se manda, también apagada, para poder quitársela. A un rol
        // que ya las tiene, apagada: no se le pone, y si la tenía guardada
        // (de un rol anterior), se le quita.
        if (!esTablet) payload.notas_administracion = ofreceNotasAdministracion && !!form.notas_administracion;
        // «Drive: entra en estas carpetas» (27/10/2026): siempre se manda,
        // también vacía, para poder quitárselas. Si las carpetas no se
        // pudieron leer, va lo que tenía (no cambia nada). Al admin, vacía.
        if (!esTablet) payload.drive_carpetas = ofreceDriveCarpetas ? driveCarpetas : [];
        // «Drive: todo (como el administrador)» (3/11/2026): siempre se manda,
        // también apagada, para poder quitársela. Al admin, apagada: no se le
        // pone, y si la tenía guardada (de un rol anterior), se le quita.
        if (!esTablet) payload.drive_todo = ofreceDriveTodo && !!form.drive_todo;
        if (form.password) payload.password = form.password;
        if ('active' in form) payload.active = form.active;
        await api.put(`/api/users/${initial.id}`, payload);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/api/users', form);
        toast.success(`Usuario creado · contraseña inicial: ${form.password}`);
      }
      onSaved && onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
          {!isEdit && (
            <DialogDescription className="text-xs">
              Se genera una <strong>contraseña provisional aleatoria</strong> (puedes cambiarla).
              El usuario deberá <strong>cambiarla en el primer login</strong>.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input required type="email" disabled={isEdit} value={form.email} onChange={(e) => setF('email', e.target.value)} data-testid="user-email" />
          </div>
          <div>
            <Label>Nombre</Label>
            <Input required value={form.name} onChange={(e) => setF('name', e.target.value)} data-testid="user-name" />
          </div>
          <div>
            <Label>Apodo (cómo le llama el Agente)</Label>
            <Input value={form.apodo || ''} onChange={(e) => setF('apodo', e.target.value)}
              placeholder="Ej.: Pili, Jefa… vacío = su nombre" maxLength={40}
              data-testid="user-apodo" />
          </div>
          <div>
            <Label>Cómo debe tratarle el Agente</Label>
            <textarea value={form.agente_notas || ''} onChange={(e) => setF('agente_notas', e.target.value)}
              rows={3} maxLength={600}
              placeholder="Ej.: informática muy básica; explicarle todo paso a paso, sin tecnicismos, y ofrecerse a preparárselo"
              className="mt-1.5 w-full rounded-md border border-input bg-white p-2 text-sm"
              data-testid="user-agente-notas" />
          </div>
          <div>
            <Label>Rol</Label>
            {esTablet ? (
              <p className="text-sm mt-1.5" data-testid="user-role-tablet">
                {roleLabel(initial.role)} · la cuenta del iPad: firma consentimientos o rellena la LOPD, según el modo que se elija en la tablet.
              </p>
            ) : (
              <Select value={form.role} onValueChange={(v) => setF('role', v)}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          {/* Quién sale en Indicadores (dirección, 04/10/2026: solo recepción y
              administración, «ningún médico»). Por rol no se podía: hay admins
              en administración y también lo es el director. Va por persona y
              sin nadie marcado salen todos (services/equipo_indicadores). Solo
              al editar: en el alta no hace falta. */}
          {isEdit && !esTablet ? (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                checked={!!form.en_indicadores}
                onChange={(e) => setF('en_indicadores', e.target.checked)}
                data-testid="user-en-indicadores"
              />
              <span>
                <span className="font-medium text-[#13231E]">Sale en Indicadores</span>
                <span className="block text-xs text-muted-foreground">
                  Sus citas, presupuestos y leads cuentan en Indicadores, también los de meses pasados. Si no se marca a nadie, salen todos.
                </span>
              </span>
            </label>
          ) : null}

          {/* Chat de dirección sin ser admin (dirección, 16/10/2026: «eso no
              cambia sus permisos, simplemente está en el chat»). */}
          {ofreceChatDireccion ? (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                checked={!!form.chat_direccion}
                onChange={(e) => setF('chat_direccion', e.target.checked)}
                data-testid="user-chat-direccion"
              />
              <span>
                <span className="font-medium text-[#13231E]">Chat de dirección</span>
                <span className="block text-xs text-muted-foreground">
                  Lee y escribe en la sala de dirección del chat, además de en la global. No cambia sus permisos en nada más.
                </span>
              </span>
            </label>
          ) : null}

          {/* «Mejoras software» (20/10/2026): la única llave de esa página. Se
              ofrece a todo el equipo, admin incluidos: ahí el rol no abre
              nada (a diferencia del chat de dirección). */}
          {isEdit && !esTablet ? (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                checked={!!form.mejoras_software}
                onChange={(e) => setF('mejoras_software', e.target.checked)}
                data-testid="user-mejoras-software"
              />
              <span>
                <span className="font-medium text-[#13231E]">Mejoras software</span>
                <span className="block text-xs text-muted-foreground">
                  Entra en «Mejoras software»: escribe las mejoras del programa que hace la rutina automática y ve lo que contesta. Ser administrador no la da por sí solo.
                </span>
              </span>
            </label>
          ) : null}

          {/* «Edita las tarifas de externos» (23/10/2026): sin nombres en el
              código, una casilla por persona. Se la da otro admin (nadie a
              sí mismo) y queda en el rastro; el servidor da 403 a quien no la
              tiene. */}
          {ofreceTarifasExternos ? (
            <label className={`flex items-start gap-2 text-sm ${tarifasBloqueada ? 'opacity-80' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                checked={!!form.edita_tarifas_externos}
                disabled={tarifasBloqueada}
                onChange={(e) => setF('edita_tarifas_externos', e.target.checked)}
                data-testid="user-edita-tarifas-externos"
              />
              <span>
                <span className="font-medium text-[#13231E]">Edita las tarifas de externos</span>
                <span className="block text-xs text-muted-foreground">
                  Cambia el bloque «Alquiler quirófano externos» del Catálogo, con el que se factura a los cirujanos externos. El resto solo lo consulta. Ser administrador no la da por sí solo.
                  {tarifasBloqueada ? ' Te la tiene que dar otro administrador.' : ''}
                </span>
              </span>
            </label>
          ) : null}

          {/* «Gestiona los bloques del Catálogo» (28/10/2026): los bloques
              principales del Catálogo de precios (el del médico titular, el
              del otro centro…) los crea y da de baja solo quien la tiene. Sin
              nombres en el código; se la da otro admin y queda en el rastro. */}
          {ofreceBloquesCatalogo ? (
            <label className={`flex items-start gap-2 text-sm ${bloquesBloqueada ? 'opacity-80' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                checked={!!form.gestiona_bloques_catalogo}
                disabled={bloquesBloqueada}
                onChange={(e) => setF('gestiona_bloques_catalogo', e.target.checked)}
                data-testid="user-gestiona-bloques-catalogo"
              />
              <span>
                <span className="font-medium text-[#13231E]">Gestiona los bloques del Catálogo</span>
                <span className="block text-xs text-muted-foreground">
                  Crea, renombra y da de baja los bloques principales del Catálogo de precios (los grandes apartados por médico o por centro). Las cirugías, los tratamientos y los médicos de dentro los sigue editando administración. Ser administrador no la da por sí solo.
                  {bloquesBloqueada ? ' Te la tiene que dar otro administrador.' : ''}
                </span>
              </span>
            </label>
          ) : null}

          {/* «Leads: solo de estas campañas» (administración, 22/10/2026): a
              quien su rol no entra en Leads (un doctor). Con alguna marcada,
              entra en Leads para VER solo los leads de esas campañas, sin
              poder cambiar a otra ni tocar nada. Sin ninguna, como su rol. */}
          {ofreceLeadsCampanas ? (
            <div className="text-sm" data-testid="user-leads-campanas">
              <span className="font-medium text-[#13231E]">Leads: solo de estas campañas</span>
              <span className="block text-xs text-muted-foreground">
                Con alguna marcada, entra en Leads para ver solo los leads de esas campañas, sin poder cambiar a otra ni tocar nada. Sin ninguna, como su rol.
              </span>
              {campanasLeads.estado === 'cargando' ? (
                <p className="text-xs text-muted-foreground mt-1.5">Cargando las campañas…</p>
              ) : campanasLeads.estado === 'fallo' ? (
                <p className="text-xs text-amber-700 mt-1.5" data-testid="user-leads-campanas-fallo">
                  No se han podido leer las campañas: se queda como estaba.
                </p>
              ) : opcionesCampanasLeads.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1.5">No hay campañas dadas de alta en Campañas.</p>
              ) : (
                <div className="mt-1.5 max-h-40 overflow-y-auto rounded-md border border-input p-2 space-y-1">
                  {opcionesCampanasLeads.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#2E6E5E]"
                        checked={leadsCampanas.includes(c.id)}
                        onChange={() => alternarCampanaLeads(c.id)}
                        data-testid={`user-leads-campana-${c.id}`}
                      />
                      <span>
                        {c.nombre || '(campaña sin nombre)'}
                        {c.estado && c.estado !== 'activa' ? (
                          <span className="text-xs text-muted-foreground">{` · ${c.estado}`}</span>
                        ) : null}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {/* «Notas de administración: también esta persona» (27/10/2026): a
              quien su rol no las tiene. Con la casilla, lee y escribe las
              notas de administración de los pacientes que ya abre. */}
          {ofreceNotasAdministracion ? (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                checked={!!form.notas_administracion}
                onChange={(e) => setF('notas_administracion', e.target.checked)}
                data-testid="user-notas-administracion"
              />
              <span>
                <span className="font-medium text-[#13231E]">Notas de administración: también esta persona</span>
                <span className="block text-xs text-muted-foreground">
                  Lee y escribe las notas de administración de los pacientes que ya puede abrir, como administración. No cambia sus permisos en nada más.
                </span>
              </span>
            </label>
          ) : null}

          {/* «Drive: todo (como el administrador)» (3/11/2026): a quien su
              rol no tiene Drive (el admin lo tiene todo). Marcada, entra en
              ClinicPilot Drive entero, como el admin: todas las carpetas, la
              bandeja general, organiza carpetas y ve quién entra en cada una.
              Va encima de las carpetas, que con ella no hacen falta. */}
          {ofreceDriveTodo ? (
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                checked={!!form.drive_todo}
                onChange={(e) => setF('drive_todo', e.target.checked)}
                data-testid="user-drive-todo"
              />
              <span>
                <span className="font-medium text-[#13231E]">Drive: todo (como el administrador)</span>
                <span className="block text-xs text-muted-foreground">
                  Entra en ClinicPilot Drive entero, sin restricción: todas las carpetas y la bandeja general, organiza carpetas y ve quién entra en cada una. No cambia sus permisos en nada más.
                </span>
              </span>
            </label>
          ) : null}

          {/* «Drive: entra en estas carpetas» (27/10/2026): a quien su rol no
              tiene Drive (el admin lo tiene todo). Con alguna marcada, entra
              en ClinicPilot Drive solo con esas carpetas y lo que cuelga de
              ellas: ve, sube, renombra y borra documentos; no organiza
              carpetas. Sin ninguna, como su rol. Con «Drive: todo» marcada
              (3/11/2026) no hace falta elegir: la lista se tapa. */}
          {ofreceDriveCarpetas ? (
            <div className="text-sm" data-testid="user-drive-carpetas">
              <span className="font-medium text-[#13231E]">Drive: entra en estas carpetas</span>
              <span className="block text-xs text-muted-foreground">
                Con alguna marcada, entra en ClinicPilot Drive solo con esas carpetas (y las que cuelgan de ellas): ve, sube, renombra y borra documentos; no organiza carpetas. Sin ninguna, como su rol.
              </span>
              {driveTodoMarcado ? (
                <p className="text-xs text-muted-foreground mt-1.5" data-testid="user-drive-carpetas-tapadas">
                  Con «Drive: todo» entra en todas las carpetas: no hace falta elegir ninguna. Si la quitas, volverán a contar las que tuviera marcadas.
                </p>
              ) : carpetasDrive.estado === 'cargando' ? (
                <p className="text-xs text-muted-foreground mt-1.5">Cargando las carpetas…</p>
              ) : carpetasDrive.estado === 'fallo' ? (
                <p className="text-xs text-amber-700 mt-1.5" data-testid="user-drive-carpetas-fallo">
                  No se han podido leer las carpetas del Drive: se queda como estaba.
                </p>
              ) : opcionesCarpetasDrive.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1.5">No hay carpetas en el Drive: créala antes desde ClinicPilot Drive.</p>
              ) : (
                <div className="mt-1.5 max-h-40 overflow-y-auto rounded-md border border-input p-2 space-y-1">
                  {opcionesCarpetasDrive.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#2E6E5E]"
                        checked={driveCarpetas.includes(c.id)}
                        onChange={() => alternarCarpetaDrive(c.id)}
                        data-testid={`user-drive-carpeta-${c.id}`}
                      />
                      <span>{c.ruta || '(carpeta sin nombre)'}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          {/* Aquí estuvo la casilla «Puede borrar episodios» (permiso por
              persona, 22/09/2026). Se quitó con la decisión D4 de dirección ese
              mismo día: «episodios solo puede borrar administradores, igual
              que gestionar usuarios o crear usuarios». Lo da el rol Admin de
              arriba; una casilla que ya no cambia nada sería una mentira. */}

          <div>
            <Label>{isEdit ? 'Nueva contraseña (opcional)' : 'Contraseña inicial'}</Label>
            <Input
              type="text"
              value={form.password || ''}
              onChange={(e) => setF('password', e.target.value)}
              placeholder={isEdit ? 'Dejar en blanco para no cambiar' : 'Contraseña provisional'}
              data-testid="user-password"
            />
            {esTablet ? (
              <p className="text-xs text-muted-foreground mt-1" data-testid="user-password-tablet">
                Mínimo {LARGO_MINIMO_TABLET} caracteres. La tablet no la cambia: es la que se teclea en el iPad. {AVISO_CLAVE_CORTA_TABLET}
              </p>
            ) : isEdit && form.password && (
              <p className="text-xs text-amber-700 mt-1">Al cambiar la contraseña, el usuario deberá actualizarla en su próximo login.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="user-save">
              <Save className="w-4 h-4 mr-2" />{isEdit ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ open, onOpenChange, target, onDone }) {
  const [newPwd, setNewPwd] = useState(randomPwd);
  const [showResult, setShowResult] = useState(false);
  const [copied, setCopied] = useState(false);
  // La tablet (29/9/2026): 6 caracteres le bastan y no se le pide cambiarla.
  const esTablet = esCuentaDeTablet(target);
  const minimo = largoMinimoDeClave(target);

  useEffect(() => {
    if (open) { setNewPwd(randomPwd()); setShowResult(false); setCopied(false); }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const corta = claveCorta(target, newPwd);
    if (corta) { toast.error(corta); return; }
    try {
      await api.post(`/api/users/${target.id}/reset-password`, { new_password: newPwd });
      setShowResult(true);
      onDone && onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error reseteando');
    }
  };

  const copyPwd = async () => {
    try {
      await navigator.clipboard.writeText(newPwd);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resetear contraseña — {target.name}</DialogTitle>
          <DialogDescription className="text-xs">
            {esTablet ? (
              <>Es la contraseña que se teclea en el iPad: la tablet <strong>no la cambia</strong> al entrar.</>
            ) : (
              <>
                El usuario tendrá que <strong>cambiar la contraseña en su próximo login</strong>.
                Comparte la contraseña provisional sólo por un canal seguro.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {!showResult ? (
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Contraseña propuesta</Label>
              <Input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                minLength={minimo}
                required
                data-testid="user-reset-pwd-input"
              />
              <p className="text-xs text-muted-foreground mt-1" data-testid="user-reset-pwd-minimo">
                Mínimo {minimo} caracteres.{esTablet ? ` ${AVISO_CLAVE_CORTA_TABLET}` : ''}
              </p>
            </div>
            {/* 16/10/2026: se daba por puesta la propuesta sin pulsar «Resetear»
                (cada vez que se abre la ventana propone otra) y a dos usuarios
                les llegó una contraseña que no valía. Lo dice antes de nada. */}
            <p
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="user-reset-pwd-aun-no"
            >
              <strong>Todavía no está puesta.</strong> Solo vale cuando pulses «Resetear»; si cierras
              esta ventana, {target.name} sigue con la contraseña de antes. Cada vez que se abre la
              ventana se propone otra distinta.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cerrar sin cambiar</Button>
              <Button type="submit" className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="user-reset-pwd-confirm">
                <KeyRound className="w-4 h-4 mr-2" /> Resetear
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-300 rounded-md p-3">
              <div className="font-semibold text-green-900 mb-1">Contraseña reseteada: ya vale esta</div>
              <p className="text-xs text-green-900">
                {esTablet
                  ? <>Es la nueva contraseña de <strong>{target.name}</strong>: la que se teclea en el iPad.</>
                  : <>Comparte esta contraseña con <strong>{target.name}</strong>. Al iniciar sesión, se le pedirá cambiarla.</>}
              </p>
            </div>
            <div className="bg-muted/40 border border-border rounded-md p-3 flex items-center justify-between">
              <code className="text-sm font-mono">{newPwd}</code>
              <Button size="sm" variant="outline" onClick={copyPwd} data-testid="user-reset-pwd-copy">
                {copied ? <><Check className="w-4 h-4 mr-1" /> Copiado</> : <><Copy className="w-4 h-4 mr-1" /> Copiar</>}
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]">Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  useEffect(() => { if (user && user.role !== 'admin') nav('/'); }, [user, nav]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/users');
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const onDelete = async (it) => {
    if (!window.confirm(`¿Desactivar a ${it.name}? El usuario ya no podrá iniciar sesión.`)) return;
    try {
      await api.delete(`/api/users/${it.id}`);
      toast.success('Usuario desactivado');
      fetchItems();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error');
    }
  };

  const openReset = (it) => {
    setResetTarget(it);
    setResetOpen(true);
  };

  return (
    <div>
      <PageHeader
        title="Usuarios"
        subtitle={`${items.length} usuarios del equipo · gestión solo para administradores`}
        actions={(
          <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="user-new">
            <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
          </Button>
        )}
      />
      <div className="cjo-card overflow-hidden">
        {loading ? (
          <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={UserCog} title="Sin usuarios" description="Añade el primero." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[rgba(46,110,94,0.04)] text-left">
                <tr>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Nombre</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Email</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Rol</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Estado</th>
                  <th className="py-3 px-4 font-semibold text-[#13231E]">Alta</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.filter(u => u.active !== false).map(u => (
                  <tr key={u.id} className="border-t border-border hover:bg-[rgba(46,110,94,0.03)]" data-testid="user-row">
                    <td className="py-3 px-4">
                      <div className="font-medium text-[#0B1220]">{u.name}</div>
                      <div className="text-[10px] text-muted-foreground" data-testid="user-login">
                        login: <code className="bg-muted/40 px-1 rounded">{loginDe(u)}</code>
                        {/* El correo vale SIEMPRE, se tenga usuario corto o no
                            (21/9: al ponerles usuario a todos, este aviso se
                            habría apagado para toda la pantalla). */}
                        {' · o su correo'}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs text-muted-foreground">{u.email}</td>
                    <td className="py-3 px-4 text-xs uppercase tracking-wider">{roleLabel(u.role)}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {u.active === false ? (
                          <Badge variant="secondary" className="bg-red-50 text-red-800 border-red-300">Desactivado</Badge>
                        ) : (
                          <Badge variant="secondary" className="bg-green-50 text-green-800 border-green-300">Activo</Badge>
                        )}
                        {u.must_change_password && (
                          <Badge variant="secondary" className="bg-amber-50 text-amber-900 border-amber-300" title="Debe cambiar la contraseña en el próximo login">
                            <KeyRound className="w-3 h-3 mr-1" /> Cambio pendiente
                          </Badge>
                        )}
                        {/* Sale en Indicadores (04/10/2026): se ve de un vistazo quién compite. */}
                        {/* En la sala de dirección del chat sin ser admin (16/10/2026). */}
                        {u.chat_direccion === true && (u.role || '').toLowerCase() !== 'admin' && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title="Está en la sala de dirección del chat" data-testid="user-badge-chat-direccion">
                            Chat dirección
                          </Badge>
                        )}
                        {/* Entra en «Mejoras software» (20/10/2026). */}
                        {u.mejoras_software === true && !esCuentaDeTablet(u) && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title="Entra en «Mejoras software»" data-testid="user-badge-mejoras">
                            Mejoras software
                          </Badge>
                        )}
                        {/* Lee y escribe las notas de administración sin que su rol
                            se las dé (27/10/2026). */}
                        {u.notas_administracion === true && !esCuentaDeTablet(u)
                          && !canSeeInternalNotes({ role: u.role }) && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title="Lee y escribe las notas de administración de los pacientes"
                            data-testid="user-badge-notas-administracion">
                            Notas de administración
                          </Badge>
                        )}
                        {/* Entra en todo el Drive, como el admin (3/11/2026). */}
                        {driveTodo(u) && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title="Entra en todo ClinicPilot Drive, como el administrador"
                            data-testid="user-badge-drive-todo">
                            Drive: todo
                          </Badge>
                        )}
                        {/* Entra en unas carpetas del Drive (27/10/2026). Con
                            «Drive: todo» no cuentan: no se enseñan. */}
                        {Array.isArray(u.drive_carpetas) && u.drive_carpetas.length > 0 && !esCuentaDeTablet(u)
                          && !isAdmin(u) && !driveTodo(u) && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title={`Entra en ${u.drive_carpetas.length === 1 ? '1 carpeta' : `${u.drive_carpetas.length} carpetas`} del Drive`}
                            data-testid="user-badge-drive-carpetas">
                            {`Entra en ${u.drive_carpetas.length === 1 ? '1 carpeta' : `${u.drive_carpetas.length} carpetas`} del Drive`}
                          </Badge>
                        )}
                        {/* Edita las tarifas de externos del Catálogo (23/10/2026). */}
                        {u.edita_tarifas_externos === true && !esCuentaDeTablet(u) && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title="Edita las tarifas de externos del Catálogo" data-testid="user-badge-tarifas-externos">
                            Tarifas externos
                          </Badge>
                        )}
                        {/* Gestiona los bloques principales del Catálogo de precios (28/10/2026). */}
                        {u.gestiona_bloques_catalogo === true && !esCuentaDeTablet(u) && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title="Crea y da de baja los bloques principales del Catálogo de precios" data-testid="user-badge-bloques-catalogo">
                            Bloques del Catálogo
                          </Badge>
                        )}
                        {/* Solo ve los leads de unas campañas (22/10/2026). */}
                        {Array.isArray(u.leads_campanas) && u.leads_campanas.length > 0 && !esCuentaDeTablet(u)
                          && !rolEntraEnLeads(u.role) && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title={`Solo ve los leads de ${u.leads_campanas.length === 1 ? '1 campaña' : `${u.leads_campanas.length} campañas`}`}
                            data-testid="user-badge-leads-campanas">
                            {`Leads: ${u.leads_campanas.length === 1 ? '1 campaña' : `${u.leads_campanas.length} campañas`}`}
                          </Badge>
                        )}
                        {u.en_indicadores === true && (
                          <Badge variant="secondary" className="bg-[rgba(46,110,94,0.08)] text-[#2E6E5E] border-[#2E6E5E]/30"
                            title="Sale en Indicadores" data-testid="user-badge-indicadores">
                            Indicadores
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-xs">{formatDate(u.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => { setEditing(u); setOpen(true); }} title="Editar" data-testid="user-edit">
                          <Edit3 className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openReset(u)} title="Resetear contraseña" data-testid="user-reset">
                          <KeyRound className="w-3.5 h-3.5" />
                        </Button>
                        {u.id !== user?.id && u.active !== false && (
                          <Button size="sm" variant="ghost" onClick={() => onDelete(u)} className="text-destructive" title="Desactivar" data-testid="user-delete">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <UserDialog open={open} onOpenChange={setOpen} initial={editing} onSaved={fetchItems} />
      <ResetPasswordDialog open={resetOpen} onOpenChange={setResetOpen} target={resetTarget} onDone={fetchItems} />
    </div>
  );
}
